import { cx } from "shared/utils";
import { LiteTextEditor } from "shared/app/Editor/LiteTextEditor";

import { DetailTabPopovers } from "./DetailTabPopovers";
import { TimelineSection } from "./TimelineSection";

import {
  useRef,
  useImperativeHandle,
  forwardRef,
  useState,
  useEffect
} from "react";

import { User } from "views/settings-users/types";
import { EditorRefApi } from "shared/components/Editor";
import { DetailTabData } from "../types";
import { extractEntitiesFromJson } from "shared/app/ActivityLog/helpers";

type DetailTabProps = {
  mode: "create" | "view";
  assignees: User[];
  data: DetailTabData | null;
  onDataChange: (data: DetailTabData) => void;
};

export type DetailTabRef = {
  blurDescriptionEditor: () => void;
};

export const DetailTab = forwardRef<DetailTabRef, DetailTabProps>(
  ({ data, onDataChange, assignees }, ref) => {
    const descriptionEditorRef = useRef<EditorRefApi>(null);
    const [isDescriptionEditing, setIsDescriptionEditing] = useState(false);

    useImperativeHandle(ref, () => ({
      blurDescriptionEditor: () => {
        descriptionEditorRef.current?.blur();
      }
    }));

    useEffect(() => {
      if (isDescriptionEditing && descriptionEditorRef.current)
        setTimeout(() => {
          descriptionEditorRef.current?.focus("end");
        }, 0);
    }, [isDescriptionEditing]);
    const handleDescriptionChange = (json: object, html: string) => {
      const text = descriptionEditorRef.current?.getDocument().text || "";
      const entities = extractEntitiesFromJson(json);
      onDataChange({
        ...data,
        description: {
          document_text: text,
          document_html: html,
          entities
        }
      } as DetailTabData);
    };

    return (
      <div className="flex-1 overflow-y-auto px-6 py-5">
        <DetailTabPopovers
          data={data}
          onDataChange={onDataChange}
          assignees={assignees}
        />

        <div className="my-4 h-px w-full bg-alpha-40" />

        <TimelineSection
          createdAt={data?.created_at}
          updatedAt={data?.updated_at}
          completedAt={data?.completed_at}
          completedBy={data?.completed_by}
          createdBy={data?.created_by}
          assignees={assignees}
        />

        <div className="my-4 h-px w-full bg-alpha-40" />

        <div className="space-y-2">
          <span className="text-sm text-content-400">Description</span>
          {!isDescriptionEditing && data?.description?.document_html ? (
            <div
              onClick={() => setIsDescriptionEditing(true)}
              className="cursor-pointer"
            >
              <LiteTextEditor
                id="task-description-view"
                editable={false}
                value={data.description.document_html}
                containerClassName={cx(
                  "rounded-lg border border-alpha-100 bg-content-100-inverted shadow-xs",
                  "px-[14px] py-2.5 min-h-[100px]"
                )}
              />
            </div>
          ) : (
            <LiteTextEditor
              ref={descriptionEditorRef}
              id="task-description"
              editable={true}
              propertyName="description"
              placeholder="You can add description here"
              value={data?.description?.document_html ?? ""}
              onChange={(json: object, html: string) => {
                handleDescriptionChange(json, html);
              }}
              onEditModeChange={(isEditing) => {
                setIsDescriptionEditing(isEditing);
              }}
              autoExpandConfig={{
                autoExpand: true,
                maxHeight: 300,
                allowMultiline: true,
                preventNewlines: false
              }}
              containerClassName={cx(
                "rounded-lg border border-alpha-100 bg-content-100-inverted shadow-xs",
                "px-[14px] py-2.5 min-h-[100px]",
                "[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-base",
                "[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-content-200",
                "[&_.ProseMirror_p.is-editor-empty:first-child::before]:font-medium"
              )}
            />
          )}
        </div>
      </div>
    );
  }
);

DetailTab.displayName = "DetailTab";
